import { motion } from 'framer-motion';
import React from 'react';
import {
  FiClock,
  FiGlobe,
  FiHeart,
  FiShield,
  FiStar,
  FiTarget,
  FiTrendingUp,
  FiUsers
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import NewsletterSection from '../components/NewsletterSection';

const About = () => {
  const whyChooseUs = [
    {
      icon: FiUsers,
      title: "Expert Team",
      description: "Work with a handpicked team of 50+ tech professionals, including AI innovators and industry veterans—each with a proven record of building solutions that deliver results.",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: FiStar,
      title: "Quality You Can Trust",
      description: "Our 98% client satisfaction speaks for itself. Every project undergoes meticulous quality checks to ensure we deliver nothing short of excellence—every time.",
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: FiClock,
      title: "On-Time, Every Time",
      description: "We respect your timelines. With agile delivery and precision project management, 95% of our projects go live exactly when you need them to.",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: FiShield,
      title: "Reliable Partnerships",
      description: "We don't just complete projects—we build long-term relationships based on transparency, trust, and shared success. We’re in it for the long haul.",
      color: "from-pink-500 to-red-600"
    },
    {
      icon: FiTrendingUp,
      title: "Results That Matter",
      description: "Over 50+ successful projects delivered across industries—backed by measurable impact, happy clients, and repeat engagements.",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: FiGlobe,
      title: "Global Support, Local Sensibility",
      description: "Whether you’re across the street or across the globe, our team is available 24/7 with the tools, flexibility, and cultural awareness to support your needs.",
      color: "from-cyan-500 to-blue-600"
    }
  ];

  const companyValues = [
    {
      title: "Innovation",
      description: "Embracing cutting-edge technologies and creative solutions",
      icon: "💡"
    },
    {
      title: "Excellence",
      description: "Delivering exceptional quality in every project and interaction",
      icon: "⭐"
    },
    {
      title: "Integrity",
      description: "Building trust through honest communication and ethical practices",
      icon: "🤝"
    },
    {
      title: "Growth",
      description: "Continuous learning and development for our team and clients",
      icon: "📈"
    }
  ];

  const stats = [
    { number: "50+", label: "Projects Completed" },
    { number: "50+", label: "Expert Consultants" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "3+", label: "Years of Excellence" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
              About Innofusion
            </span>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
              Transforming Business
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Through Expert Talent
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              At Innofusion, we bridge the gap between ambitious business goals and exceptional talent. 
              Since our founding, we've been dedicated to helping organizations achieve their digital transformation through innovative solutions and expert professionals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Company Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
                IFA
                <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                  Story
                </span>
              </h2>
              <div className="space-y-6 text-gray-600 font-body leading-relaxed">
                <p>
                At InnoFusion Analytics, we were born from real-world consulting and enterprise experience—having led high-impact projects for some of the most respected organizations across industries. That journey motivated us to create something different: a firm that blends cutting-edge innovation with grounded, practical delivery.
                </p>
                <p>
                  What sets us apart is our people. We bring together young, bright minds who thrive in emerging technologies like AI, alongside seasoned domain experts who understand the nuances of complex industries. This fusion of fresh perspective and proven expertise allows us to build solutions that are not only innovative, but highly effective.
                </p>
                <p>
                  We believe that great ideas deserve world-class execution—regardless of a client’s size or technical background. Whether it’s a solo founder bringing their first product to life or a Fortune 500 firm undergoing digital transformation, we treat every engagement as a partnership in success.
                </p>
                <p>
                At IFA, we don’t just deliver solutions. We build lasting value—with integrity, empathy, and a shared sense of purpose.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent font-display">
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-600 font-body mt-1">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Mission & Vision Cards */}
              <div className="space-y-8">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-3xl border border-blue-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <FiTarget className="text-white" size={24} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 font-display tracking-tight">IFA Mission</h3>
                  </div>
                  <p className="text-gray-700 font-body leading-relaxed">
                  To empower individuals and organizations—regardless of technical expertise—to bring their ideas to life through intelligent solutions, future-ready talent, and a deep commitment to collaboration and value creation.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-3xl border border-purple-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                      <FiHeart className="text-white" size={24} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 font-display tracking-tight">IFA Vision</h3>
                  </div>
                  <p className="text-gray-700 font-body leading-relaxed">
                    To be the most trusted technology partner in the world—celebrated for our innovation, people-first values, and ability to turn complex challenges into meaningful progress through the power of AI, talent, and domain expertise.

                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Why Choose
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Innofusion Analytics
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              We bring together industry expertise, cutting-edge innovation, and a client-first mindset to help you succeed faster, smarter, and with confidence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((reason, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 h-full group-hover:scale-105">
                  <div className={`w-16 h-16 bg-gradient-to-br ${reason.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <reason.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 font-heading tracking-wide">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 font-body leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              IFA
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Core Values
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              What guides us, grounds us, and drives us forward—every project, every client, every day.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100"
              >
                <div className="text-4xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 font-heading tracking-wide">
                  {value.title}
                </h3>
                <p className="text-gray-600 font-body leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Partner with Us?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how Innofusion can help transform your business with expert talent and innovative solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Get In Touch
                </motion.button>
              </Link>
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 font-accent tracking-wide"
                >
                  Explore Our Services
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <NewsletterSection />
      <Footer />
    </div>
  );
};

export default About; 